import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import useStore from "components/ui/Context"
import cn from "classnames"
import DieuKhoanBaoMat from "components/Policy/DieuKhoanBaoMat"
import ChinhSachBaoMat from "components/Policy/ChinhSachBaoMat"
import ChinhSachBaoVeDuLieuCaNhan from "components/Policy/ChinhSachBaoVeDuLieuCaNhan"
import ChinhSachBaoVeThongTinKhachHang from "components/Policy/ChinhSachBaoVeThongTinKhachHang"

const TABS = [
  { label: "Điều khoản Bảo mật", Component: DieuKhoanBaoMat },
  { label: "Chính sách Bảo mật", Component: ChinhSachBaoMat },
  { label: "Bảo vệ Dữ liệu Cá nhân", Component: ChinhSachBaoVeDuLieuCaNhan },
  { label: "Bảo vệ Thông tin Khách hàng", Component: ChinhSachBaoVeThongTinKhachHang }
]

export default function PolicyConsentModal({
  onConfirm,
  onCancel,
  initialTab = 0,
  source = "login" // "login" | "footer" | "policy"
}) {
  const { userInfo } = useStore()

  const isLoggedIn = userInfo.id ? true : false

  const [isClient, setClient] = useState(false)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [accepted, setAccepted] = useState(false)
  const scrollRef = useRef(null)

  // Sync activeTab with initialTab prop
  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  // Pre-check when logged in (user đã đăng nhập)
  useEffect(() => {
    if (userInfo.id) {
      setAccepted(true)
    }
  }, [userInfo])

  useEffect(() => {
    setClient(true)
  }, [])

  // Đóng popup theo Esc nhưng không để lan tới handler Esc của modal login
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.keyCode === 27) {
        event.stopImmediatePropagation()
        onCancel?.()
      }
    }
    document.addEventListener("keydown", handleEscape, true)
    return () => document.removeEventListener("keydown", handleEscape, true)
  }, [onCancel])

  // Cuộn lên đầu khi đổi tab
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [activeTab])

  // Khoá cuộn body khi modal mở
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [])

  const ActiveComponent = TABS[activeTab].Component

  // Hiển thị buttons khi chưa đăng nhập hoặc từ login
  const showButtons = !isLoggedIn || source === "login"

  // Close button chỉ hiện khi từ footer + đã đăng nhập
  const showCloseButton = source === "footer"

  // Checkbox disabled khi đã đăng nhập
  const checkboxDisabled = isLoggedIn

  const modalContent = (
    <div className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onCancel} />

      <div className="relative z-10 flex h-full w-full flex-col lg:pt-[0]">
        {/* Close button - only show when from footer + logged in */}
        {showCloseButton && (
          <div className="flex justify-end px-4 py-4 md:px-10 lg:px-44 xl:px-80">
            <button
              onClick={onCancel}
              className="w-10 h-10 rounded-full bg-[#1B1B1B] flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-black text-white">
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row p-4 md:px-10 lg:px-44 xl:px-80">
            <div className="flex shrink-0 gap-2 overflow-x-auto lg:hidden mb-4 [scrollbar-width:thin]">
              {TABS.map((tab, index) => (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(index)}
                  className={cn(
                    "whitespace-nowrap px-3 py-3 text-xs font-medium sm:text-sm text-secondary",
                    activeTab === index ? "text-[#FAFAF9] border-b-2 border-white" : "hover:text-primary"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <aside className="hidden lg:flex lg:w-72 lg:shrink-0 lg:flex-col lg:gap-1 lg:px-4">
              {TABS.map((tab, index) => (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(index)}
                  className={cn(
                    "w-full h-14 rounded-lg px-4 py-2 text-left text-sm font-medium bg-[#1B1B1B]",
                    activeTab === index ? "!bg-primary text-[#FAFAF9]" : "text-secondary hover:bg-white/10"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </aside>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3 lg:p-4 bg-[#1A1A1A] rounded-lg">
              <div
                ref={scrollRef}
                style={{ WebkitOverflowScrolling: "touch" }}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_p]:mb-3"
              >
                <ActiveComponent isShow={isClient} />
              </div>
            </div>
          </div>

          {/* Footer - always show checkbox */}
          {source != "policy" && (
            <div className="flex shrink-0 flex-col gap-3 bg-[#232323] p-4 md:py-9 md:px-10 lg:px-44 xl:px-80">
              <label
                className={cn(
                  "flex items-start gap-2 text-xs leading-[18px] sm:text-sm",
                  checkboxDisabled ? "cursor-default" : "cursor-pointer"
                )}
              >
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => !checkboxDisabled && setAccepted(e.target.checked)}
                  disabled={checkboxDisabled}
                  className="mt-1 h-4 w-4 shrink-0 accent-primary"
                />
                <p>
                  <span>Tôi đã đọc và đồng ý với </span>
                  <span className="inline text-primary underline font-bold">
                    Điều khoản bảo mật, Chính sách bảo mật, Chính sách bảo vệ dữ liệu cá nhân, Chính sách bảo vệ thông
                    tin khách hàng.
                  </span>
                </p>
              </label>

              {/* Action buttons - show when not logged in OR from login */}
              {showButtons && (
                <div className="flex flex-col gap-3 justify-center items-center">
                  <button
                    onClick={onCancel}
                    className="h-11 flex-1 rounded-xl bg-[#2E2E2E] text-[#FAFAF9] hover:bg-white/10 p-3 md:p-4 w-full md:w-96"
                  >
                    Hủy bỏ & Trở về
                  </button>
                  <button
                    onClick={onConfirm}
                    disabled={!accepted}
                    className={cn(
                      "h-11 flex-1 rounded-xl font-medium p-3 md:p-4 w-full md:w-96",
                      accepted ? "bg-primary text-[#FAFAF9] hover:bg-primary-hover" : "bg-[#2E2E2E] text-[#5F5F5F]"
                    )}
                  >
                    Xác nhận và Tiếp tục
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  if (isClient) {
    return createPortal(modalContent, document.getElementById("modal-root"))
  }
  return null
}
